<script setup lang="ts">
import { Form, type FormSubmitEvent } from '@primevue/forms'
import { Button, DatePicker, FloatLabel, InputMask, InputText, Message, Textarea } from 'primevue'
import { type Ref, onMounted, ref } from 'vue'
import Header from '@/components/Header.vue'
import IconCard from '@/components/IconCard.vue'
import NumberBlock from '@/components/NumberBlock.vue'
import { PHONE } from '@/constants'
import { useProductStore } from '@/stores'
import { type FormData, type ProductStore } from '@/types'
import {
    cleanPhone,
    dayjs,
    formResolver,
    isDesktop,
    maxBirthDate,
    minBirthDate,
    sendLandingForm,
} from '@/utils'
import Footer from '@/components/ui/Footer.vue'

const productStore: ProductStore = useProductStore()

const tform: Ref<number> = ref(0)
const bform: Ref<number> = ref(0)

const formInitial: FormData = {
    birthday: null,
    comment: null,
    email: null,
    name: null,
    phone: null,
}

const tbirthday: Ref<Date | null> = ref(null)
const bbirthday: Ref<Date | null> = ref(null)

const goToApp = (content: string): void => {
    window.location.href =
        `${import.meta.env.VITE_LK_HOSTNAME}/register?` +
        `utm_source=landing&utm_medium=button&utm_campaign=landing_default&utm_content=${content}`
}

const sendForm = async (form: FormSubmitEvent, source: 'top' | 'bottom'): Promise<void> => {
    const bday = source === 'top' ? tbirthday.value : bbirthday.value
    await sendLandingForm({
        birthday: dayjs(bday).isValid()
            ? dayjs(bday).format('YYYY-MM-DD')
            : dayjs(bday, 'DD.MM.YYYY').format('YYYY-MM-DD'),
        comment: form.states.comment.value,
        email: form.states.email.value,
        name: form.states.name.value,
        phone: cleanPhone(form.states.phone.value as string),
    })
    if (source === 'top') {
        tbirthday.value = null
        tform.value += 1
    } else {
        bbirthday.value = null
        bform.value += 1
    }
}

onMounted(async () => {
    await productStore.getMatrix()
})
</script>

<template>
    <Header @on-button-click="goToApp" />

    <div class="flex flex-col gap-4 px-4 py-10 text-center md:gap-6 md:px-20 md:py-15 lg:px-50">
        <div class="flex flex-col gap-4 md:gap-3">
            <div class="text-mob-h2 text-primary-500 md:text-desk-h2">
                Чем раньше обратитесь, тем меньше долг
            </div>
            <p>
                Уточним информацию, ответим на любые вопросы, сообщим номер договора и способы
                оплаты
            </p>
        </div>
        <Button
            class="h-14 self-center border-base-green bg-base-green text-button-primary-color md:w-[340px]"
            label="Остановить увеличение одним переводом"
            size="small"
            :fluid="!isDesktop"
            rounded
            @click="goToApp('early_action_cta')" />
    </div>

    <div class="flex flex-col gap-6 px-4 py-10 text-center md:gap-8 md:px-20 md:py-15 lg:px-50">
        <div class="flex flex-col gap-3">
            <div class="text-mob-h2 text-primary-500 md:text-desk-h2">Спишем до 40% долга</div>
            <p class="text-body-14 md:text-body-16">
                Не знаете, чем выплачивать — свяжитесь с нами по телефону,
                <br v-if="isDesktop" />
                мы предложим персональную программу и услуги финансового консультанта
            </p>
        </div>
        <div class="flex flex-col flex-wrap justify-center gap-3 md:flex-row">
            <IconCard icon="thumbUp" title="Гибкие условия">
                Индивидуальный подход к каждому долгу
            </IconCard>
            <IconCard icon="court" title="Приостановка взыскания">
                Возможность приостановить исполнительное производство
            </IconCard>
            <IconCard icon="discount" title="Скидки и предложения">
                Персональные скидки на сумму долга
            </IconCard>
            <IconCard icon="chart" title="Удобный график выплат">
                Платите по комфортному расписанию
            </IconCard>
        </div>
    </div>

    <div class="px-4 pb-[120px] pt-10 md:px-20 md:py-15 lg:px-50">
        <Form
            v-slot="$form"
            :key="tform"
            class="flex flex-col rounded-3xl bg-primary-400 bg-form-mob bg-full-w bg-right bg-no-repeat p-4 md:flex-row md:bg-form md:bg-full-h md:p-6"
            :initial-values="formInitial"
            :resolver="formResolver"
            validate-on-blur
            @submit="(e: FormSubmitEvent) => sendForm(e, 'top')">
            <div class="flex flex-col gap-6 lg:w-[555px]">
                <div class="mb-2 text-mob-h2 text-primary-50 md:mb-0 md:text-desk-h2">
                    Заполните небольшую анкету, а мы поможем
                </div>
                <div class="flex flex-col gap-6 md:grid md:grid-cols-2 md:gap-4">
                    <div>
                        <FloatLabel>
                            <InputText
                                id="tname"
                                name="name"
                                size="large"
                                :invalid="$form.name?.dirty && $form.name?.invalid"
                                fluid />
                            <label for="tname">ФИО</label>
                        </FloatLabel>
                        <Message
                            v-if="$form.name?.dirty && $form.name?.invalid"
                            severity="error"
                            size="small"
                            variant="simple">
                            {{ $form.name?.error.message }}
                        </Message>
                    </div>
                    <div>
                        <FloatLabel>
                            <DatePicker
                                id="tbitrhday"
                                v-model="tbirthday"
                                :max-date="maxBirthDate()"
                                :min-date="minBirthDate()"
                                size="large"
                                manual-input
                                fluid />
                            <label for="tbirthday">Дата рождения</label>
                        </FloatLabel>
                    </div>
                </div>
                <div class="flex flex-col gap-6 md:grid md:grid-cols-2 md:gap-4">
                    <div>
                        <FloatLabel>
                            <InputMask
                                id="tphone"
                                name="phone"
                                mask="+7 999 999 99-99"
                                slot-char=" "
                                size="large"
                                :invalid="$form.phone?.dirty && $form.phone?.invalid"
                                fluid />
                            <label for="tphone">Номер телефона</label>
                        </FloatLabel>
                        <Message
                            v-if="$form.phone?.dirty && $form.phone?.invalid"
                            severity="error"
                            size="small"
                            variant="simple">
                            {{ $form.phone?.error.message }}
                        </Message>
                    </div>
                    <div>
                        <FloatLabel>
                            <InputText
                                id="temail"
                                name="email"
                                size="large"
                                :invalid="$form.email?.dirty && $form.email?.invalid"
                                fluid />
                            <label for="temail">Электронная почта</label>
                        </FloatLabel>
                        <Message
                            v-if="$form.email?.dirty && $form.email?.invalid"
                            severity="error"
                            size="small"
                            variant="simple">
                            {{ $form.email?.error.message }}
                        </Message>
                    </div>
                </div>
                <FloatLabel>
                    <Textarea
                        id="tcomment"
                        name="comment"
                        class="w-full resize-none rounded-lg"
                        :rows="isDesktop ? 3 : 4"
                        size="large" />
                    <label for="tcomment">Комментарий</label>
                </FloatLabel>
                <Button
                    type="submit"
                    label="Отправить"
                    class="h-14 border-base-green bg-base-green text-button-primary-color"
                    size="large"
                    :disabled="
                        !$form.valid ||
                        !$form.name?.dirty ||
                        !tbirthday ||
                        !$form.phone?.dirty ||
                        !$form.email?.dirty
                    "
                    rounded
                    fluid />
            </div>
            <div
                class="-mb-24 mt-8 hidden shrink-0 grow items-center justify-center md:my-0 md:justify-end lg:visible lg:flex">
                <img src="@assets/page.png" class="h-56 md:mb-0 md:h-[345px]" />
            </div>
        </Form>
    </div>

    <div
        class="flex flex-col justify-center gap-6 px-4 py-10 text-center md:gap-12 md:px-50 md:py-15 md:text-left lg:flex-row lg:px-50">
        <div class="flex flex-col justify-center gap-8 md:w-[440px]">
            <div class="text-mob-h2 text-primary-500 md:text-desk-h2">
                Последствия неуплаты и отказа от диалога
            </div>
            <Button
                v-if="isDesktop"
                label="Остановить увеличение одним переводом"
                class="h-14 w-96 border-base-green bg-base-green text-button-primary-color"
                size="small"
                rounded
                fluid
                @click="goToApp('consequences_cta')" />
        </div>
        <div class="flex flex-col gap-3">
            <NumberBlock :number="1">
                Увеличение суммы долга из-за гос. пошлин и судебных расходов
            </NumberBlock>
            <NumberBlock :number="2">Формирование негативной кредитной истории</NumberBlock>
            <NumberBlock :number="3">Ограничение выезда за границу</NumberBlock>
            <NumberBlock :number="4">Арест банковских счетов и имущества</NumberBlock>
        </div>
        <Button
            v-if="!isDesktop"
            label="Остановить увеличение одним переводом"
            class="h-14 w-96 self-center border-base-green bg-base-green text-button-primary-color"
            size="small"
            rounded
            fluid
            @click="goToApp('consequences_cta')" />
    </div>

    <div class="bg-light-gray">
        <div class="px-4 py-10 text-center md:px-20 md:py-15 lg:px-50">
            <div class="mb-6 text-mob-h2 text-primary-500 md:mb-8 md:text-desk-h2">
                С нами удобно погашать долг
            </div>
            <div
                class="flex flex-col flex-wrap gap-3 rounded-3xl bg-surface-0 p-4 md:flex-row md:flex-wrap md:items-center md:justify-center lg:p-6">
                <div class="flex items-center gap-3 md:w-80 md:flex-col">
                    <div
                        class="w-fit rounded-[14px] bg-surface-50 p-2.5 text-icon md:rounded-3xl md:p-4">
                        <img src="@assets/creditCard.svg" class="h-6 md:h-10" />
                    </div>
                    <div class="text-mob-h5 md:text-mob-h3">Банковской картой</div>
                </div>
                <div class="flex items-center gap-3 md:w-80 md:flex-col">
                    <div
                        class="w-fit rounded-[14px] bg-surface-50 p-2.5 text-icon md:rounded-3xl md:p-4">
                        <img src="@assets/bank.svg" class="h-6 md:h-10" />
                    </div>
                    <div class="text-mob-h5 md:text-mob-h3">Банковский перевод</div>
                </div>
                <div class="flex items-center gap-3 md:w-80 md:flex-col">
                    <div
                        class="w-fit rounded-[14px] bg-surface-50 p-2.5 text-icon md:rounded-3xl md:p-4">
                        <img src="@assets/moneyCoins.svg" class="h-6 md:h-10" />
                    </div>
                    <div class="text-mob-h5 md:text-mob-h3">через Систему Быстрых Платежей</div>
                </div>
            </div>
        </div>

        <div
            class="flex flex-col items-center justify-between gap-4 px-4 py-10 text-center md:px-20 lg:flex-row lg:gap-16 lg:px-50 lg:py-15 lg:text-left">
            <div class="flex flex-col gap-2 md:w-[400px] md:gap-3">
                <div class="primary-500 text-mob-h2 md:text-desk-h2">
                    Оставьте заявку — мы поможем
                </div>
                <div class="text-desk-h5 text-primary-color">
                    Или позвоните по телефону<br />
                    <a class="text-nowrap" :href="'call:' + cleanPhone(PHONE)">{{ PHONE }}</a>
                </div>
                <img
                    src="@assets/heartMessage.png"
                    class="order-first w-[118px] self-center md:order-last md:w-44" />
            </div>
            <Form
                v-slot="$form"
                :key="bform"
                class="w-full md:w-[550px] lg:w-[410px]"
                :initial-values="formInitial"
                :resolver="formResolver"
                :validate-on-value-update="['birthday']"
                @submit="(e: FormSubmitEvent) => sendForm(e, 'bottom')">
                <div class="flex flex-col gap-6 rounded-3xl bg-primary-50 px-4 py-6 md:px-6">
                    <div>
                        <FloatLabel>
                            <InputText
                                id="bname"
                                name="name"
                                size="large"
                                :invalid="$form.name?.dirty && $form.name?.invalid"
                                fluid />
                            <label for="bname">ФИО</label>
                        </FloatLabel>
                        <Message
                            v-if="$form.name?.dirty && $form.name?.invalid"
                            severity="error"
                            size="small"
                            variant="simple">
                            {{ $form.name?.error.message }}
                        </Message>
                    </div>
                    <div>
                        <FloatLabel>
                            <DatePicker
                                id="tbitrhday"
                                v-model="bbirthday"
                                :max-date="maxBirthDate()"
                                :min-date="minBirthDate()"
                                size="large"
                                manual-input
                                fluid />
                            <label for="bbirthday">Дата рождения</label>
                        </FloatLabel>
                    </div>
                    <div class="flex flex-col gap-6 md:grid md:grid-cols-2 md:gap-4">
                        <div>
                            <FloatLabel>
                                <InputMask
                                    id="bphone"
                                    name="phone"
                                    mask="+7 999 999 99-99"
                                    slot-char=" "
                                    size="large"
                                    :invalid="$form.phone?.dirty && $form.phone?.invalid"
                                    fluid />
                                <label for="bphone">Номер телефона</label>
                            </FloatLabel>
                            <Message
                                v-if="$form.phone?.dirty && $form.phone?.invalid"
                                severity="error"
                                size="small"
                                variant="simple">
                                {{ $form.phone?.error.message }}
                            </Message>
                        </div>
                        <div>
                            <FloatLabel>
                                <InputText
                                    id="bemail"
                                    name="email"
                                    size="large"
                                    :invalid="$form.email?.dirty && $form.email?.invalid"
                                    fluid />
                                <label for="bemail">Электронная почта</label>
                            </FloatLabel>
                            <Message
                                v-if="$form.email?.dirty && $form.email?.invalid"
                                severity="error"
                                size="small"
                                variant="simple">
                                {{ $form.email?.error.message }}
                            </Message>
                        </div>
                    </div>
                    <FloatLabel>
                        <InputText id="bcomment" name="comment" size="large" fluid />
                        <label for="bcomment">Комментарий</label>
                    </FloatLabel>
                    <Button
                        type="submit"
                        label="Отправить"
                        class="h-14 border-base-green bg-base-green text-button-primary-color"
                        size="large"
                        :disabled="
                            !$form.valid ||
                            !$form.name?.dirty ||
                            !bbirthday ||
                            !$form.phone?.dirty ||
                            !$form.email?.dirty
                        "
                        rounded
                        fluid />
                </div>
            </Form>
        </div>

        <Footer />
    </div>
</template>
